import React, { useState, useEffect, useCallback } from 'react';
import { Fuel, Plus, Trash2, RefreshCw } from 'lucide-react';
import { Sidebar } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { dieselPricingAPI, formatApiError } from '../utils/api';
import { toast } from 'sonner';

function currentMonthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function DieselPricingPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ state: '', month: currentMonthStr(), price_per_litre: '', source: 'manual' });

  const fetchRows = useCallback(async () => {
    try {
      const res = await dieselPricingAPI.list();
      setRows(res.data || []);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load diesel prices'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleAdd = async () => {
    if (!form.state || !form.month || form.price_per_litre === '') {
      toast.error('State, month and price are required');
      return;
    }
    setSaving(true);
    try {
      await dieselPricingAPI.upsert({ ...form, price_per_litre: parseFloat(form.price_per_litre) });
      toast.success('Diesel price saved');
      setForm({ state: '', month: currentMonthStr(), price_per_litre: '', source: 'manual' });
      fetchRows();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save diesel price'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (state, month) => {
    try {
      await dieselPricingAPI.remove(state, month);
      toast.success('Entry removed');
      fetchRows();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to remove entry'));
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0 p-6 md:p-8" data-testid="diesel-pricing-page">
        <div className="flex items-center space-x-2 mb-1">
          <Fuel className="w-5 h-5 text-blue-600" />
          <h1 className="text-2xl font-bold text-slate-900">Diesel Price Reference</h1>
        </div>
        <p className="text-sm text-slate-500 mb-6">
          State + month diesel prices used to compute diesel-avoided value in performance reports.
        </p>

        <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
          <h3 className="font-semibold text-slate-900 mb-4">Add / update price</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 items-end">
            <div>
              <label className="text-[10px] text-slate-500 uppercase block mb-1">State</label>
              <input type="text" value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                placeholder="Karnataka" className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="diesel-state-input" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase block mb-1">Month</label>
              <input type="month" value={form.month} onChange={(e) => setForm((f) => ({ ...f, month: e.target.value }))}
                className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="diesel-month-input" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase block mb-1">Price (₹/litre)</label>
              <input type="number" step="0.01" value={form.price_per_litre} onChange={(e) => setForm((f) => ({ ...f, price_per_litre: e.target.value }))}
                placeholder="94.20" className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="diesel-price-input" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase block mb-1">Source</label>
              <input type="text" value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
                className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="diesel-source-input" />
            </div>
            <Button onClick={handleAdd} disabled={saving} data-testid="diesel-save-btn">
              <Plus className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">Price history</h3>
            <Button size="sm" variant="outline" onClick={fetchRows} data-testid="diesel-refresh-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-slate-400">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-sm text-slate-400">No diesel prices seeded yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="diesel-prices-table">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">State</th>
                  <th className="text-left px-4 py-2">Month</th>
                  <th className="text-right px-4 py-2">Price (₹/L)</th>
                  <th className="text-left px-4 py-2">Source</th>
                  <th className="text-left px-4 py-2">Updated by</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.state}-${r.month}`} className="border-t border-slate-100" data-testid={`diesel-row-${r.state}-${r.month}`}>
                    <td className="px-4 py-2 font-medium text-slate-800">{r.state}</td>
                    <td className="px-4 py-2 font-mono text-slate-600">{r.month}</td>
                    <td className="px-4 py-2 text-right font-mono">₹{r.price_per_litre.toFixed(2)}</td>
                    <td className="px-4 py-2 text-slate-500">{r.source}</td>
                    <td className="px-4 py-2 text-slate-500">{r.updated_by}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => handleDelete(r.state, r.month)} className="text-slate-400 hover:text-red-500"
                        data-testid={`diesel-delete-${r.state}-${r.month}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
